import * as React from 'react'
import { View, Text } from 'react-native'
import styled from '@theme/styled-components'

import MultiChoice from '@components/choice/MultiChoice'

import useI18n from '@i18n/useI18n'
import useReducer from '@store/useReducer'
import * as AdminStore from '@store/admin/admin'

import { FormikHelpers, useFormik } from 'formik'

import { TypeUtilisateurRole } from '@api/enum'

interface Values {
  scopes: string[]
}

const typeUtilisateurToRoleType: Record<TypeUtilisateur, TypeUtilisateurRole> = {
  EXPOSANT: TypeUtilisateurRole.EXPOSANT, // should not exist
  ORGANISATEUR: TypeUtilisateurRole.ORGANISATEUR,
  PRESTATAIRE: TypeUtilisateurRole.PRESTATAIRE,
  VIPARIS: TypeUtilisateurRole.VIPARIS,
}

const Scope = ({ form, onForm }: AdminUserFormProps) => {
  const init: Values = React.useMemo(() => ({ scopes: form.scopes }), [form.scopes])

  const i18n = useI18n()

  const roles = useReducer(AdminStore.store, (s) => s.roles)

  const onSubmit = (vals: Values, { setSubmitting }: FormikHelpers<Values>) => {
    onForm(vals).finally(() => setSubmitting(false))
  }

  const { values, setFieldValue, handleSubmit, dirty } = useFormik<Values>({
    enableReinitialize: true,
    initialValues: init,
    onSubmit,
  })

  React.useEffect(() => {
    if (dirty) {
      // sumbit on change
      handleSubmit()
    }
  }, [values])

  const options = React.useMemo(
    () =>
      roles
        .filter((r) => r.typeUtilisateur === typeUtilisateurToRoleType[form.typeUtilisateur])
        .map((r) => ({ label: r.name, value: r.id }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [roles, form.typeUtilisateur]
  )
  const opts = options.filter((o) => values.scopes.includes(o.value))

  return (
    <MainContainer>
      {options.length > 0 ? (
        <MultiChoice
          data={options}
          onItemSelected={(next) =>
            setFieldValue(
              'scopes',
              next.map((o) => o.value)
            )
          }
          value={opts}
          inline={false}
        />
      ) : (
        <MissingInfo>{i18n.t('screens.admin.users.form.scope.noScope')}</MissingInfo>
      )}
    </MainContainer>
  )
}

export default Scope

const MainContainer = styled(View)`
  margin: 0 ${(props) => (props.theme.breakpoint === 'phone' ? 24 : 48)}px;
  margin-top: 25px;
  flex: 1;
`

const MissingInfo = styled(Text)`
  ${(props) => props.theme.fonts.Footnote};
  font-size: 14px;
  color: ${(props) => props.theme.colors.secondaryText};
  ${(props) =>
    props.theme.breakpoint === 'phone'
      ? ` padding: 23px 16px;
          background-color: ${props.theme.colors.background};
          text-align: center;
          font-size: 12px;`
      : `padding: 16px 0px;`}
`
